import { supabase } from "@/integrations/supabase/client";
import { translateApiError } from "@/lib/auth-error-messages";

interface TemplateGenerationResponse {
  template_prompt: string;
}

export interface TemplateGenerationExampleRequest {
  document_description: string;
  document_example: string;
}

export interface TemplateGenerationExampleResponse {
  template_prompt: string;
}

/**
 * Генерирует промпт для постобработки транскрибированного текста с использованием ИИ
 * @param documentType Тип документа для создания шаблона постобработки
 * @returns Сгенерированный ИИ промпт для постобработки
 */
export const generateTemplatePrompt = async (userInput: string): Promise<string> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(import.meta.env.VITE_TEMPLATE_GENERATION_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      document_type: userInput,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
    throw new Error(translateApiError(errorMessage));
  }

  const data = await response.json();
  return data.template_prompt;
};

export const testTemplate = async (recordId: string, promptText: string): Promise<string> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(import.meta.env.VITE_TEMPLATETEST_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      record_id: recordId,
      prompt_text: promptText,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
    throw new Error(translateApiError(errorMessage));
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error("Template test failed");
  }

  return data.processed_text;
};

export const generateTemplateFromExample = async (
  description: string,
  example: string
): Promise<string> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(import.meta.env.VITE_TEMPLATE_GENERATE_EXAMPLE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      document_description: description,
      document_example: example,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data.template_prompt;
};
